import React, { useState } from "react";
import { FaInfoCircle } from "react-icons/fa";

import SidebarToolInfoModal from "../../components/SidebarToolInfoModal";
import {
  PLANTING_PACKAGING_HELP_TITLE,
  PlantingPackagingHelpContent,
} from "./plantingPackagingHelpContent";
import {
  PLANTING_PMS_HELP_TITLE,
  PlantingPmsHelpContent,
} from "./plantingPmsHelpContent";
import {
  PLANTING_FERTILIZER_CALIBRATION_HELP_TITLE,
  PlantingFertilizerCalibrationHelpContent,
} from "./plantingFertilizerCalibrationHelpContent";
import {
  PLANTING_SEED_CALIBRATION_HELP_TITLE,
  PlantingSeedCalibrationHelpContent,
} from "./plantingSeedCalibrationHelpContent";
import {
  PLANTING_SUPPLY_UNIT_HELP_TITLE,
  PlantingSupplyUnitHelpContent,
} from "./plantingSupplyUnitHelpContent";
import {
  OPERATOR_SUPPLY_UNITS,
  computeSeedsPerMeter,
  formatIntegerPtBr,
} from "./serviceOrderPlanting";
import "./ServiceOrderPlantingPanel.css";

function FieldInfoButton({ title, onClick }) {
  return (
    <button
      type="button"
      className="service-order-planting-info-btn"
      title={title}
      aria-label={title}
      onClick={onClick}
    >
      <FaInfoCircle aria-hidden />
    </button>
  );
}

function TipLinkButton({ label, onClick }) {
  return (
    <button
      type="button"
      className="service-order-planting-tip-link"
      onClick={onClick}
    >
      <FaInfoCircle aria-hidden />
      <span>{label}</span>
    </button>
  );
}

function ServiceOrderPlantingPanel({
  cultivar,
  onCultivarChange,
  seedsThousandPerHa,
  onSeedsThousandPerHaChange,
  rowSpacingCm,
  onRowSpacingCmChange,
  pmsG,
  onPmsGChange,
  seedsPerPack,
  onSeedsPerPackChange,
  operatorSupplyUnit,
  onOperatorSupplyUnitChange,
  fertilizerKgHa,
  onFertilizerKgHaChange,
  inoculantWaterLHa,
  onInoculantWaterLHaChange,
  binderRecommendation,
  onBinderRecommendationChange,
  t,
}) {
  const [showPmsHelp, setShowPmsHelp] = useState(false);
  const [showPackagingHelp, setShowPackagingHelp] = useState(false);
  const [showSupplyUnitHelp, setShowSupplyUnitHelp] = useState(false);
  const [showSeedCalibrationHelp, setShowSeedCalibrationHelp] = useState(false);
  const [showFertilizerCalibrationHelp, setShowFertilizerCalibrationHelp] =
    useState(false);
  const pmsHelpTitle = t(PLANTING_PMS_HELP_TITLE);
  const packagingHelpTitle = t(PLANTING_PACKAGING_HELP_TITLE);
  const supplyUnitHelpTitle = t(PLANTING_SUPPLY_UNIT_HELP_TITLE);
  const seedCalibrationHelpTitle = t(PLANTING_SEED_CALIBRATION_HELP_TITLE);
  const fertilizerCalibrationHelpTitle = t(
    PLANTING_FERTILIZER_CALIBRATION_HELP_TITLE
  );
  const seedsPerMeter = computeSeedsPerMeter({
    seedsThousandPerHa,
    rowSpacingCm,
  });

  return (
    <div className="service-order-planting-panel">
      <div className="service-order-planting-intro">
        <strong>{t("Recomendação de plantio")}</strong>
        <div className="service-order-planting-tips">
          <TipLinkButton
            label={t("Dica de regulagem de sementes")}
            onClick={() => setShowSeedCalibrationHelp(true)}
          />
          <TipLinkButton
            label={t("Dica de regulagem de adubo")}
            onClick={() => setShowFertilizerCalibrationHelp(true)}
          />
        </div>
      </div>

      <div className="service-order-planting-grid">
        <label className="service-order-planting-field service-order-planting-field--wide">
          {t("Cultivar")}
          <input
            className="form-control"
            value={cultivar}
            onChange={(event) => onCultivarChange(event.target.value)}
            placeholder={t("Nome da variedade")}
          />
        </label>

        <label className="service-order-planting-field">
          {t("Mil sementes/ha")}
          <input
            className="form-control"
            type="number"
            min="0"
            step="1"
            value={seedsThousandPerHa}
            onChange={(event) => onSeedsThousandPerHaChange(event.target.value)}
            placeholder="280"
          />
          <span>
            {t("Recomendação de população. Ex.: soja 280 mil sementes/ha.")}
          </span>
        </label>

        <label className="service-order-planting-field">
          {t("Espaçamento entre linhas (cm)")}
          <input
            className="form-control"
            type="number"
            min="0"
            step="0.1"
            value={rowSpacingCm}
            onChange={(event) => onRowSpacingCmChange(event.target.value)}
            placeholder="45"
          />
          <span>{t("Espaçamento da plantadeira para calcular sementes/m.")}</span>
        </label>

        <label className="service-order-planting-field">
          <span className="service-order-planting-field__label">
            {t("PMS — peso de mil sementes (g)")}
            <FieldInfoButton
              title={pmsHelpTitle}
              onClick={() => setShowPmsHelp(true)}
            />
          </span>
          <input
            className="form-control"
            type="number"
            min="0"
            step="0.1"
            value={pmsG}
            onChange={(event) => onPmsGChange(event.target.value)}
            placeholder="180"
          />
          <span>{t("Opcional. Dado do lote da semente, em gramas.")}</span>
        </label>

        <label className="service-order-planting-field">
          <span className="service-order-planting-field__label">
            {t("Sementes por embalagem")}
            <FieldInfoButton
              title={packagingHelpTitle}
              onClick={() => setShowPackagingHelp(true)}
            />
          </span>
          <input
            className="form-control"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={formatIntegerPtBr(seedsPerPack)}
            onChange={(event) =>
              onSeedsPerPackChange(event.target.value.replace(/\D/g, ""))
            }
            placeholder="5.000.000"
          />
          <span>
            {t(
              "bag/sacas. Número inteiro, com ponto de milhar. Ex.: 5.000.000."
            )}
          </span>
        </label>

        <label className="service-order-planting-field service-order-planting-field--wide">
          <span className="service-order-planting-field__label">
            {t("Operador informa o abastecimento em")}
            <FieldInfoButton
              title={supplyUnitHelpTitle}
              onClick={() => setShowSupplyUnitHelp(true)}
            />
          </span>
          <select
            className="form-control"
            value={operatorSupplyUnit}
            onChange={(event) => onOperatorSupplyUnitChange(event.target.value)}
          >
            {OPERATOR_SUPPLY_UNITS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.label)}
              </option>
            ))}
          </select>
          <span>
            {t(
              "Unidade que o operador lança no app ao abastecer a plantadeira."
            )}
          </span>
        </label>

        <label className="service-order-planting-field">
          {t("Adubo (kg/ha)")}
          <input
            className="form-control"
            type="number"
            min="0"
            step="0.1"
            value={fertilizerKgHa}
            onChange={(event) => onFertilizerKgHaChange(event.target.value)}
          />
        </label>

        <label className="service-order-planting-field">
          {t("Inoculante (L de água/ha)")}
          <input
            className="form-control"
            type="number"
            min="0"
            step="0.1"
            value={inoculantWaterLHa}
            onChange={(event) => onInoculantWaterLHaChange(event.target.value)}
          />
          <span>
            {t("Litros de água por hectare para acompanhar o que está caindo.")}
          </span>
        </label>

        <label className="service-order-planting-field service-order-planting-field--wide">
          {t("Inoculante (recomendação por hectare)")}
          <textarea
            className="form-control"
            rows={2}
            value={binderRecommendation}
            onChange={(event) => onBinderRecommendationChange(event.target.value)}
            placeholder={t("Texto livre para ir junto com a ordem")}
          />
        </label>
      </div>

      {seedsPerMeter > 0 ? (
        <div className="service-order-planting-preview">
          <p>
            <strong>{t("Sementes por metro")}:</strong>{" "}
            {seedsPerMeter.toLocaleString("pt-BR", {
              maximumFractionDigits: 1,
            })}
          </p>
        </div>
      ) : null}

      {showPmsHelp ? (
        <SidebarToolInfoModal
          title={pmsHelpTitle}
          onClose={() => setShowPmsHelp(false)}
        >
          <PlantingPmsHelpContent />
        </SidebarToolInfoModal>
      ) : null}
      {showPackagingHelp ? (
        <SidebarToolInfoModal
          title={packagingHelpTitle}
          onClose={() => setShowPackagingHelp(false)}
        >
          <PlantingPackagingHelpContent />
        </SidebarToolInfoModal>
      ) : null}
      {showSupplyUnitHelp ? (
        <SidebarToolInfoModal
          title={supplyUnitHelpTitle}
          onClose={() => setShowSupplyUnitHelp(false)}
        >
          <PlantingSupplyUnitHelpContent />
        </SidebarToolInfoModal>
      ) : null}
      {showSeedCalibrationHelp ? (
        <SidebarToolInfoModal
          title={seedCalibrationHelpTitle}
          onClose={() => setShowSeedCalibrationHelp(false)}
        >
          <PlantingSeedCalibrationHelpContent />
        </SidebarToolInfoModal>
      ) : null}
      {showFertilizerCalibrationHelp ? (
        <SidebarToolInfoModal
          title={fertilizerCalibrationHelpTitle}
          onClose={() => setShowFertilizerCalibrationHelp(false)}
        >
          <PlantingFertilizerCalibrationHelpContent />
        </SidebarToolInfoModal>
      ) : null}
    </div>
  );
}

export default ServiceOrderPlantingPanel;
